import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Dropdown, Input, Modal, Space, Table, Tag, Typography } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { useAuth } from "contexts/AuthContext";
import useOrganizations from "hooks/olreadia/useBusinessEntities";
import useOrganizationLocationTypes from "hooks/olreadia/useBusinessLocationTypes";
import CopyableText from "components/common/CopyableText";
import OrganizationDrawer from "./OrganizationDrawer";
import { formatEnumLabel } from "../constants";
import { profileService } from "services/profileService";


const statusColor = (status) => {
  if (!status) return "default";
  if (status === "ACTIVE" || status === "APPROVED") return "green";
  if (status === "DRAFT" || status === "PENDING_REVIEW") return "gold";
  if (status === "SUSPENDED" || status === "DECLINED") return "red";
  if (status === "DELETED") return "default";
  return "blue";
};

const formatText = (text, maxLength = 30) => {
  const value = text ? String(text) : "-";
  if (value === "-") return value;
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
};

const toErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (data?.errors && typeof data.errors === "object") {
    const validationMessage = Object.values(data.errors)
      .filter(Boolean)
      .map(String)
      .join(", ");
    if (validationMessage) return validationMessage;
  }
  return error?.message || fallback;
};

export default function OrganizationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";

  const { organizations, loading, error, page, list, create, update, remove } =
    useOrganizations();
  const { locationTypes, locationTypesLoading, fetchLocationTypes } =
    useOrganizationLocationTypes();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [query, setQuery] = useState("");
  const [approvalLoadingId, setApprovalLoadingId] = useState(null);
  const [decliningOrganization, setDecliningOrganization] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [suspendingOrganization, setSuspendingOrganization] = useState(null);
  const [suspensionReason, setSuspensionReason] = useState("");


  const fetchPage = async (pag = pagination) => {
    await list({ page: pag.current - 1, size: pag.pageSize });
  };

  useEffect(() => {
    fetchPage();
    fetchLocationTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => fetchPage();

  const handleCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleEdit = (record) => {
    setEditing(record);
    setDrawerOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Remove organization?",
      content: "This will deactivate/remove the organization.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        await remove(record.id, actorId);
        await fetchPage();
      },
    });
  };

  const updateApproval = async (record, status, reason) => {
    const id = resolveOrganizationId(record);
    if (!id) return;
    setApprovalLoadingId(id);
    try {
      await profileService.organizations.update(
        id,
        {
          status,
          ...(status === "DECLINED" && reason
            ? { declineReason: reason.trim() }
            : {}),
          ...(status === "SUSPENDED" && reason
            ? { suspensionReason: reason.trim() }
            : {}),
        },
        actorId,
      );
      const messages = {
        APPROVED: "Organization approved",
        DECLINED: "Organization declined",
        SUSPENDED: "Organization suspended",
        PENDING_REVIEW: "Organization moved to pending review",
      };
      toast.success(messages[status] || "Organization status updated");
      setDecliningOrganization(null);
      setDeclineReason("");
      setSuspendingOrganization(null);
      setSuspensionReason("");
      await fetchPage();
    } catch (err) {
      toast.error(toErrorMessage(err, "Failed to update organization status"));
      return false;
    } finally {
      setApprovalLoadingId(null);
    }
  };

  const confirmApproval = (record) => {
    Modal.confirm({
      title: "Approve organization?",
      content: `Are you sure you want to approve ${record?.name || "this organization"}?`,
      okText: "Yes, approve",
      cancelText: "No",
      zIndex: 2600,
      onOk: () => updateApproval(record, "APPROVED"),
    });
  };

  const confirmStatusChange = (record, status, title, content, okText, danger = false) => {
    Modal.confirm({
      title,
      content,
      okText,
      cancelText: "Cancel",
      okButtonProps: { danger },
      zIndex: 2600,
      onOk: () => updateApproval(record, status),
    });
  };

  const handleSubmit = async (payload) => {
    if (editing?.id) {
      await update(editing.id, payload, actorId);
    } else {
      await create(payload, actorId);
    }
    setDrawerOpen(false);
    await fetchPage();
  };

  const resolveOrganizationId = (record) =>
    record?.id ||
    record?.organizationId ||
    record?.entityId ||
    record?.uuid ||
    null;

  const filteredOrganizations = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (organizations || []).filter((e) => {
      if (e?.status === "DELETED") return false;
      if (!q) return true;
      const id = resolveOrganizationId(e) ? String(resolveOrganizationId(e)) : "";
      const status = e?.status ? String(e.status) : "";
      const name = e?.name ? String(e.name) : "";
      const ein = e?.ein ? String(e.ein) : "";
      return (
        id.toLowerCase().includes(q) ||
        name.toLowerCase().includes(q) ||
        status.toLowerCase().includes(q) ||
        ein.toLowerCase().includes(q)
      );
    });
  }, [organizations, query]);

  const columns = useMemo(
    () => [
      {
        title: "ID",
        dataIndex: "id",
        key: "id",
        ellipsis: true,
        render: (value, record) => {
          const id = resolveOrganizationId(record) || value;
          return id ? <CopyableText value={id} showTooltip={false} /> : "-";
        },
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        ellipsis: true,
        render: (value) => <Typography.Text>{formatText(value, 14)}</Typography.Text>,
      },
      {
        title: "Email",
        key: "contact",
        ellipsis: true,
        render: (_, record) => {
          const email = record?.contactInformation?.primaryEmail;
          return (
            <div style={{ lineHeight: 1.2 }}>
              <Typography.Text style={{ display: "block" }} ellipsis>
                {email || "-"}
              </Typography.Text>
            </div>
          );
        },
      },
      {
        title: "Address",
        key: "location",
        ellipsis: true,
        render: (_, record) => {
          const addressLine1 = record?.locationInformation?.addressLine1;
          return <Typography.Text>{formatText(addressLine1, 24)}</Typography.Text>;
        },
      },
      {
        title: "EIN",
        dataIndex: "ein",
        key: "ein",
        ellipsis: true,
        render: (val) => <Typography.Text>{formatText(val, 9)}</Typography.Text>,
      },
      {
        title: "Location Type",
        dataIndex: "organizationLocationType",
        key: "organizationLocationType",
        ellipsis: true,
        render: (val) => formatEnumLabel(val?.code || val) || "-",
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (val) => <Tag color={statusColor(val)}>{formatEnumLabel(val)}</Tag>,
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <div className="be-actions" onClick={(e) => e.stopPropagation()}>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "view",
                    label: "View",
                    icon: <EyeOutlined />,
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      const id = resolveOrganizationId(record);
                      if (!id) return;
                      navigate(paths.organizationDetails(id), {
                        state: { entity: record },
                      });
                    },
                  },
                  {
                    key: "edit",
                    label: "Edit",
                    icon: <EditOutlined />,
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      handleEdit(record);
                    },
                  },
                  ...(["ACTIVE", "DRAFT", "PENDING_REVIEW", "SUSPENDED"].includes(record?.status)
                    ? [
                        {
                          key: "approve",
                          label: "Approve",
                          icon: <CheckOutlined />,
                          disabled:
                            approvalLoadingId === resolveOrganizationId(record),
                          onClick: ({ domEvent }) => {
                            domEvent.stopPropagation();
                            confirmApproval(record);
                          },
                        },
                        ...(record?.status !== "SUSPENDED"
                          ? [{
                              key: "decline",
                              label: "Decline",
                              icon: <CloseOutlined />,
                              danger: true,
                              disabled:
                                approvalLoadingId === resolveOrganizationId(record),
                              onClick: ({ domEvent }) => {
                                domEvent.stopPropagation();
                                setDecliningOrganization(record);
                                setDeclineReason("");
                              },
                            }]
                          : []),
                      ]
                    : []),
                  ...(record?.status === "APPROVED"
                    ? [{
                        key: "suspend",
                        label: "Suspend",
                        icon: <StopOutlined />,
                        danger: true,
                        onClick: ({ domEvent }) => {
                          domEvent.stopPropagation();
                          setSuspendingOrganization(record);
                          setSuspensionReason("");
                        },
                      }]
                    : []),
                  ...(record?.status === "DECLINED"
                    ? [{
                        key: "pending-review",
                        label: "Pending Review",
                        onClick: ({ domEvent }) => {
                          domEvent.stopPropagation();
                          confirmStatusChange(
                            record,
                            "PENDING_REVIEW",
                            "Move to pending review?",
                            `Move ${record?.name || "this organization"} back to pending review?`,
                            "Move to review",
                          );
                        },
                      }]
                    : []),
                  {
                    key: "remove",
                    label: "Remove",
                    icon: <DeleteOutlined />,
                    danger: true,
                    onClick: ({ domEvent }) => {
                      domEvent.stopPropagation();
                      handleDelete(record);
                    },
                  },
                ],
              }}
            >
              <Button
                size="small"
                type="text"
                aria-label="Actions"
                loading={approvalLoadingId === resolveOrganizationId(record)}
              >
                <EllipsisOutlined style={{ fontSize: 18 }} />
              </Button>
            </Dropdown>
          </div>
        ),
      },
    ],
    [navigate, approvalLoadingId],
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 mt-2 gap-3">
        <WidgetSectionTitle
          icon="building"
          title="Organizations"
          subtitle="Create and manage organizations, their locations, services, and users."
          className="mb-2 mt-2 flex-1"
        /> 
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            style={{ paddingInline: 14 }}
          >
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Organization
          </Button>
        </Space>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load"
          description={error}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Table
        rowKey={(record) => resolveOrganizationId(record) || record?.name}
        loading={loading}
        columns={columns}
        dataSource={filteredOrganizations}
        className="custom-table"
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        size="small"
        style={{ borderRadius: 10, overflow: "hidden", marginTop: 8 }}
        title={() => (
          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
            <Input.Search
              allowClear
              placeholder="Search by ID, name, status, EIN"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 320 }}
            />
          </div>
        )}
        
        pagination={{
          ...pagination,
          total: page.totalElements,
          size: "small",
          showSizeChanger: true,
          pageSizeOptions: ["20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={(pag) => {
          const next = { current: pag.current, pageSize: pag.pageSize };
          setPagination(next);
          list({ page: next.current - 1, size: next.pageSize });
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if (e?.target?.closest?.(".be-actions")) return;
            if (e?.target?.closest?.(".ant-table-row-expand-icon")) return;
            if (e?.target?.closest?.(".ant-table-row-expand-icon-cell")) return;
            const id = resolveOrganizationId(record);
            if (!id) return;
            navigate(paths.organizationDetails(id), { state: { entity: record } });
          },
        })}
        expandable={undefined}
        locale={{
          emptyText: "No organizations found for the selected filters.",
        }}
      />

      <OrganizationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        loading={loading}
        initialValues={editing}
        locationTypes={locationTypes}
        locationTypesLoading={locationTypesLoading}
      />

      <Modal
        title={`Decline ${decliningOrganization?.name || "organization"}?`}
        open={Boolean(decliningOrganization)}
        zIndex={2600}
        okText="Decline"
        cancelText="Cancel"
        okButtonProps={{ danger: true, disabled: !declineReason.trim() }}
        cancelButtonProps={{ disabled: Boolean(approvalLoadingId) }}
        confirmLoading={
          approvalLoadingId === resolveOrganizationId(decliningOrganization)
        }
        onOk={() =>
          updateApproval(decliningOrganization, "DECLINED", declineReason)
        }
        onCancel={() => {
          setDecliningOrganization(null);
          setDeclineReason("");
        }}
      >
        <p style={{ marginBottom: 12 }}>
          Please provide a reason. This will be saved with the declined organization.
        </p>
        <Input.TextArea
          value={declineReason}
          onChange={(event) => setDeclineReason(event.target.value)}
          placeholder="Enter the reason for declining this organization"
          rows={4}
          maxLength={2000}
          showCount
          style={{ marginBottom: 24 }}
        />
      </Modal>

      <Modal
        title={`Suspend ${suspendingOrganization?.name || "organization"}?`}
        open={Boolean(suspendingOrganization)}
        zIndex={2600}
        okText="Suspend"
        cancelText="Cancel"
        okButtonProps={{ danger: true, disabled: !suspensionReason.trim() }}
        cancelButtonProps={{ disabled: Boolean(approvalLoadingId) }}
        confirmLoading={
          approvalLoadingId === resolveOrganizationId(suspendingOrganization)
        }
        onOk={() =>
          updateApproval(suspendingOrganization, "SUSPENDED", suspensionReason)
        }
        onCancel={() => {
          setSuspendingOrganization(null);
          setSuspensionReason("");
        }}
      >
        <p style={{ marginBottom: 12 }}>
          Please provide a reason. This will be saved with the suspended organization.
        </p>
        <Input.TextArea
          value={suspensionReason}
          onChange={(event) => setSuspensionReason(event.target.value)}
          placeholder="Enter the reason for suspending this organization"
          rows={4}
          maxLength={2000}
          showCount
          style={{ marginBottom: 24 }}
        />
      </Modal>
    </div>
  );
}
